// src/components/layouts/ReviewerSidebar.jsx
import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { 
  ClipboardCheck, 
  CheckCircle, 
  LogOut,
  Clock,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Menu,
  Home,
  Star,
  Award,
  FileText,
  Loader
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from '../../utils/axios';

const ReviewerSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  
  // State for sidebar collapsed status
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isActive, setIsActive] = useState(location.pathname);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    pendingReviews: 0,
    completedReviews: 0,
    overdueReviews: 0,
    averageScore: 0,
    urgentReviews: 0,
    highPriorityReviews: 0,
    dueThisWeek: 0
  });
  
  // State for mobile menu
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // Fetch reviewer stats
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/reviewer/stats');
        if (response.data.success) {
          setStats(response.data.stats);
        }
      } catch (error) {
        console.error('Error fetching reviewer stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const navItems = [
    { 
      path: '/reviewerdash', 
      icon: Home, 
      label: 'Reviewer Dashboard',
      badge: null,
    },
    { 
      path: '/reviewerdash/pending', 
      icon: Clock, 
      label: 'In Progress',
      badge: stats.pendingReviews,
      badgeColor: 'bg-yellow-100 text-yellow-700'
    },
    { 
      path: '/reviewerdash/overdue', 
      icon: AlertCircle, 
      label: 'Overdue Reviews',
      badge: stats.overdueReviews,
      badgeColor: 'bg-red-100 text-red-700'
    },
    { 
      path: '/reviewerdash/submitted', 
      icon: CheckCircle, 
      label: 'Completed Reviews',
      badge: stats.completedReviews,
      badgeColor: 'bg-green-100 text-green-700'
    },
  ];

  const handleLogout = async () => {  
    await logout();
    navigate('/portal/login');
  };

  // Toggle sidebar collapse
  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Mobile menu toggle
  const toggleMobileMenu = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  // Close sidebar on mobile
  const closeSidebar = () => {
    setIsMobileOpen(false);
  };

  // Get user initials
  const getUserInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`;
    }
    if (user?.fullname) {
      const names = user.fullname.split(' ');
      if (names.length >= 2) {
        return `${names[0][0]}${names[1][0]}`;
      }
      return names[0][0];
    }
    return 'RV';
  };

  // Get user full name
  const getUserFullName = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user?.fullname) {
      return user.fullname;
    }
    return 'Reviewer Name';
  };

  // Get user ORCID
  const getUserOrcid = () => {
    return user?.orcidId || user?.orcid || 'Not provided';
  };

  // Calculate completion rate
  const totalReviews = stats.completedReviews + stats.pendingReviews;
  const completionRate = totalReviews > 0 
    ? Math.round((stats.completedReviews / totalReviews) * 100) 
    : 0;

  return (
    <>
      {/* Mobile Menu Button (visible on small screens) */}
      <button
        onClick={toggleMobileMenu}
        className="lg:hidden fixed top-20 left-4 z-50 bg-purple-600 text-white p-2 rounded-lg shadow-lg"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Overlay for mobile */}
      {isMobileOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-gray-600 bg-opacity-50 z-40"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          ${isCollapsed ? 'w-20' : 'w-72'} 
          bg-white shadow-xl
          fixed lg:relative
          ${isMobileOpen ? 'left-0' : '-left-72 lg:left-0'}
          top-0 h-screen z-50
          flex flex-col
          transition-all duration-300 ease-in-out
        `}
      >
        <div className="h-full flex flex-col">
          {/* Sidebar Header */}
          <div className="p-4 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center justify-between">
              {isCollapsed ? (
                <div className="w-full flex justify-center">
                  <div className="w-10 h-10 bg-gradient-to-br from-[--asfi-blue] to-[--asfi-purple]  rounded-lg flex items-center justify-center">
                    <ClipboardCheck className="w-5 h-5 text-white" />
                  </div>
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                 <div className="w-10 h-10 bg-gradient-to-br from-[--asfi-blue] to-[--asfi-purple] rounded-lg flex items-center justify-center flex-shrink-0">
                    <img src="/assets/images/favicon.png" alt="ASFI" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-lg font-semibold text-gray-800 truncate">Reviewer Dashboard</h2>
                    <p className="text-xs text-gray-500 truncate">Peer Review Portal</p>
                  </div>
                </div>
              )}
              
              {/* Collapse toggle button - visible on lg screens */}
              <button 
                onClick={toggleSidebar}
                className="hidden lg:flex text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"
              >
                {isCollapsed ? 
                  <ChevronRight className="w-5 h-5" /> : 
                  <ChevronLeft className="w-5 h-5" />
                }
              </button>
              
              {/* Close button for mobile */}
              <button 
                onClick={closeSidebar}
                className="lg:hidden text-gray-500 hover:text-gray-700"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* User Profile Summary */}
          <div className="px-4 py-4 border-b border-gray-200 flex-shrink-0">
            <div className={`flex ${isCollapsed ? 'flex-col items-center' : 'items-center'} space-y-${isCollapsed ? '2' : '0'} p-3 bg-gray-50 rounded-lg`}>
              <div className="w-12 h-12 bg-gradient-to-br from-[--asfi-blue] to-[--asfi-purple]  rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
                <span className="text-white font-semibold text-lg">
                  {getUserInitials()}
                </span>
              </div>
              {!isCollapsed && (
                <div className="flex-1 min-w-0 ml-3">
                  <h3 className="font-semibold text-gray-800 truncate">
                    {getUserFullName()}
                  </h3>
                  <p className="text-xs text-gray-500 mt-1 truncate flex items-center">
                    <Award className="w-3 h-3 mr-1 text-purple-600" />
                    ORCID: {getUserOrcid()}
                  </p>
                  {user?.affiliations && (
                    <p className="text-xs text-gray-400 mt-1 truncate" title={user.affiliations}>
                      {user.affiliations}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Navigation - Scrollable area */}
          <nav className="flex-1 overflow-y-auto py-4 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
            <ul className="space-y-1 px-3">
              {navItems.map((item) => (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    onClick={closeSidebar}
                    className={({ isActive }) =>
                      `flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} px-4 py-3 rounded-lg transition-all group relative ${
                        isActive 
                ? 'bg-gradient-to-r from-[--asfi-blue] to-[--asfi-purple] text-white shadow-sm' 
                : 'text-gray-700 hover:bg-purple-50'
                      }`
                    }
                    title={isCollapsed ? item.label : ''}
                  >
                    <div className="flex items-center">
                      <item.icon className={`w-5 h-5 flex-shrink-0 ${
                         isActive ? 'text-purple' : 'text-purple-500 group-hover:text-[--asfi-blue]'
                      }`} />
                      {!isCollapsed && (
                        <span className="ml-3 text-sm font-medium truncate">{item.label}</span>
                      )}
                    </div>
                    {!isCollapsed && item.badge !== null && item.badge > 0 && (
                      <span className={`ml-2 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold ${
                       isActive
                          ? 'bg-white text-purple-700'
                          : 'bg-purple-100 text-purple-700'
                      }`}>
                        {item.badge}
                      </span>
                    )}
                    {isCollapsed && item.badge !== null && item.badge > 0 && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                        {item.badge}
                      </span>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>

            {/* Reviewer Stats - Inside scrollable area */}
            {!isCollapsed && (
              <div className="mt-6 px-3">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                    <Star className="w-4 h-4 mr-2 text-purple-600" /> Reviewer Stats
                  </h3>
                  
                  {loading ? (
                    <div className="flex justify-center py-4">
                      <Loader className="w-5 h-5 animate-spin text-purple-600" />
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {/* Completion Rate */}
                      <div>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-gray-600">Completion Rate</span>
                          <span className="font-medium text-purple-600">
                            {completionRate}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div 
                            className="bg-purple-600 h-1.5 rounded-full"
                            style={{ width: `${completionRate}%` }}
                          />
                        </div>
                      </div>
                      
                      {/* Average Score */}
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Average Score</span>
                        <span className="font-medium text-purple-600">{stats.averageScore}/5</span>
                      </div>
                      
                      {/* Counts Grid */}
                      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-200">
                        <div className="text-center">
                          <p className="text-lg font-bold text-purple-600">{stats.completedReviews}</p>
                          <p className="text-xs text-gray-500">Completed</p>
                        </div>
                        <div className="text-center">
                          <p className="text-lg font-bold text-yellow-600">{stats.pendingReviews}</p>
                          <p className="text-xs text-gray-500">Pending</p>
                        </div>
                      </div>
                      
                      {/* Priority Breakdown */}
                      {(stats.urgentReviews > 0 || stats.highPriorityReviews > 0 || stats.dueThisWeek > 0) && (
                        <div className="pt-2 border-t border-gray-200">
                          <p className="text-xs font-medium text-gray-600 mb-2">Priority Breakdown</p>
                          <div className="space-y-1">
                            {stats.urgentReviews > 0 && (
                              <div className="flex justify-between text-xs">
                                <span className="text-red-600">Urgent</span>
                                <span className="font-medium text-gray-700">{stats.urgentReviews}</span>
                              </div>
                            )}
                            {stats.highPriorityReviews > 0 && (
                              <div className="flex justify-between text-xs">
                                <span className="text-orange-600">High Priority</span>
                                <span className="font-medium text-gray-700">{stats.highPriorityReviews}</span>
                              </div>
                            )}
                            {stats.dueThisWeek > 0 && (
                              <div className="flex justify-between text-xs">
                                <span className="text-yellow-600">Due This Week</span>
                                <span className="font-medium text-gray-700">{stats.dueThisWeek}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Overdue Alert */}
                      {stats.overdueReviews > 0 && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-center">
                          <p className="text-xs font-medium text-red-600">
                            {stats.overdueReviews} Overdue Review{stats.overdueReviews > 1 ? 's' : ''}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </nav>

          {/* Logout Button - Fixed at bottom */}
          <div className="p-4 border-t border-gray-200 flex-shrink-0">
            <button
              onClick={handleLogout}
              className={`flex items-center ${isCollapsed ? 'justify-center' : ''} w-full px-4 py-3 rounded-lg hover:bg-red-50 transition-colors group`}
              title={isCollapsed ? 'Log Out' : ''}
            >
              <span className={`w-5 h-5 flex items-center justify-center flex-shrink-0 ${
                isCollapsed ? '' : 'mr-3'
              }`}>
                <LogOut className="w-4 h-4 text-red-500" />
              </span>
              {!isCollapsed && (
                <span className="text-sm font-medium text-gray-700 group-hover:text-red-600">Log Out</span>
              )}
            </button>

            {/* Footer text - Only when expanded */}
            {!isCollapsed && (
              <div className="text-center mt-4">
                <p className="text-xs text-gray-500">
                  Peer Review System
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  &copy; {new Date().getFullYear()} ASFIRJ
                </p>
                {user?.asfiMembershipId && (
                  <p className="text-xs text-gray-400 mt-1">
                    Member ID: {user.asfiMembershipId}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};

export default ReviewerSidebar;